import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Collection} from "types/Collection";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {prefetchNearbyPages} from "services/query-client";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {CollectionIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Dropdown from "components/Dropdown";
import {truncate} from "services/string";
import DeleteCollection from "pages/Collection/Delete";
import {useOperator} from "context/OperatorContext";

// Component ----

function CollectionList() {
  const [page, setPage] = useState<number>(1);
  const result = useQuery(['collections.all', {page}], () => ngage.collections.all({page}), {keepPreviousData: true})

  const [deletingCollection, setDeletingCollection] = useState<Collection | null>(null);

  const {termFor} = useOperator()
  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prefetch next and previous pages
  prefetchNearbyPages('collections.all', page, ngage.collections.all)

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/collections/new')}>New</Button>

  // Prepare breadcrumbs
  const breadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Collections')]: '/collections'
  };

  return (
    <Page title={termFor('Collections')} actions={actions} breadcrumbs={breadcrumbs}>
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>About</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Exhibits')}</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <CollectionIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('collections')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((collection, index) => (
              <TableRow key={collection.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <Dropdown position="left">
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/collections/${collection.id}`)},
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/collections/${collection.id}/edit`)},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingCollection(collection)}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <CollectionIcon className="h-5 w-5 mr-2 text-gray-500"/> {collection.name}
                    </div>
                  </div>
                </TableData>
                <TableData transform={TableData.transforms.CAPITALIZE}>{truncate(collection.about, 50)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{collection.exhibit_count}</TableData>
                <TableData>{formattedDateTime(collection.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/collections/${collection.id}`)},
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/collections/${collection.id}/edit`)},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingCollection(collection)}
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteCollection
                  collection={deletingCollection}
                  show={collection === deletingCollection}
                  onCancel={() => setDeletingCollection(null)}
                  onDelete={collection => {
                    navigate('/collections')
                    notify("success", `${termFor('Collection')} Deleted`, `${collection.name} has been permanently removed`)
                  }}
                  onDeleteFail={collection => {
                    setDeletingCollection(null)
                    notify("error", `${termFor('Collection')} Not Deleted`, `${collection.name} could not be removed`)
                  }}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default CollectionList;